// Join readiness for pending member invites (Platform membership ops Slice 1).
// Derived labels only. Never claim mailbox delivery. Auth-exists is knowable only from
// the member-auth-invite Edge outcome in this browser session.

export type InviteAuthHint = "invite_email_sent" | "already_registered" | null;

export type JoinReadinessTone = "warn" | "neutral" | "proof";

export interface JoinReadiness {
  readonly label: string;
  readonly tone: JoinReadinessTone;
}

export const JOIN_READINESS_PENDING_DEFAULT =
  "Reserved · Auth mail may be needed" as const;

export const JOIN_READINESS_ALREADY_REGISTERED =
  "Reserved · use Login code or password" as const;

/** Fail-closed join readiness for a management invite row. */
export function joinReadinessForInvite(input: {
  status: string;
  authHint?: InviteAuthHint;
}): JoinReadiness {
  if (input.status !== "pending") {
    if (input.status === "accepted") {
      return { label: "Joined", tone: "proof" };
    }
    if (input.status === "revoked") {
      return { label: "Revoked", tone: "neutral" };
    }
    if (input.status === "expired") {
      return { label: "Expired", tone: "neutral" };
    }
    return { label: JOIN_READINESS_PENDING_DEFAULT, tone: "warn" };
  }
  if (input.authHint === "already_registered") {
    return { label: JOIN_READINESS_ALREADY_REGISTERED, tone: "warn" };
  }
  return { label: JOIN_READINESS_PENDING_DEFAULT, tone: "warn" };
}

export interface InviteTruthCheckItem {
  readonly id: string;
  readonly title: string;
  readonly detail: string;
  readonly tone: JoinReadinessTone;
}

/** Checklist copy for the Why can't they get in? drawer. */
export function inviteTruthChecklist(input: {
  status: string;
  expiresAt: string | null;
  authHint?: InviteAuthHint;
  nowMs?: number;
}): readonly InviteTruthCheckItem[] {
  const readiness = joinReadinessForInvite({
    status: input.status,
    authHint: input.authHint ?? null,
  });
  const items: InviteTruthCheckItem[] = [
    {
      id: "email-match",
      title: "They must sign in with the invited email",
      detail: "The join key is the normalized invite address. A different mailbox cannot accept this invite.",
      tone: "neutral",
    },
    {
      id: "reservation",
      title: "Pending means membership is reserved",
      detail: "It is not proof they can open Console or Guard yet, and it is not proof mail reached an inbox.",
      tone: "warn",
    },
    {
      id: "auth-mail",
      title: readiness.label,
      detail:
        input.authHint === "already_registered"
          ? "This session learned that email already has a sign-in account. They can use Login code or password."
          : "Resend sign-in email retries Auth mail for this pending row. It does not create a second reservation.",
      tone: readiness.tone,
    },
  ];

  if (input.expiresAt !== null) {
    const expiresMs = Date.parse(input.expiresAt);
    const now = input.nowMs ?? Date.now();
    const expired = Number.isFinite(expiresMs) && expiresMs <= now;
    items.push({
      id: "expiry",
      title: expired ? "This invite is past expiry" : "This invite expires",
      detail: expired
        ? "Revoke is unnecessary if the host already expired it; Invite again after a fresh Send invite."
        : `Expires ${new Date(input.expiresAt).toLocaleString()}. After that date they need a new invite.`,
      tone: expired ? "warn" : "neutral",
    });
  }

  return items;
}
